import { apiFetch } from './http';
import { fetchProfileBlocks, invalidateAllCaches } from './server';
import {
  toChannel,
  type BlockPreview,
  type Channel,
  type ChannelRow,
  type CreateChannelInput,
  type UpdateChannelInput,
} from './types';

// --- Block preview TTL cache ---
const PREVIEW_CACHE_TTL = 30_000;
const MAX_PREVIEW_CACHE = 50;

interface PreviewCacheEntry {
  data: BlockPreview[];
  expiry: number;
}

const blockPreviewCache = new Map<string, PreviewCacheEntry>();

export function clearBlockPreviewCache(): void {
  blockPreviewCache.clear();
}

type ChannelListRow = Omit<ChannelRow, 'blocks'> & { blocks_count?: number };

function toChannelFromList(row: ChannelListRow): Channel {
  return toChannel({ ...row, blocks: [{ count: row.blocks_count ?? 0 }] });
}

/** Owner's channels (requires auth), ordered by position. */
export async function fetchChannels(): Promise<Channel[]> {
  const rows = await apiFetch<ChannelListRow[]>('/api/channels');
  return rows.map(toChannelFromList);
}

// Fetches block previews for channel cards.
// - With `handle`: uses the public profile-blocks route (owner sees all
//   channels' blocks, visitors see public only — same visibility rules the
//   old RLS reads enforced).
// - Without `handle` (explore page): uses the public explore feed, which
//   returns the newest public blocks across all users.
// Callers should pre-filter channelIds to only include visible channels.
export async function fetchBlockPreviews(
  channelIds: string[],
  options?: { handle?: string; accessToken?: string },
): Promise<BlockPreview[]> {
  if (!channelIds.length) return [];

  const cacheKey = `${options?.handle || '_explore'}:${[...channelIds].sort().join(',')}`;
  const entry = blockPreviewCache.get(cacheKey);
  if (entry && Date.now() < entry.expiry) return entry.data;

  const wanted = new Set(channelIds);
  let result: BlockPreview[];

  if (options?.handle) {
    const { blocks } = await fetchProfileBlocks(options.handle, options.accessToken);
    result = blocks
      .filter((b) => wanted.has(b.channelId))
      .map((b) => ({
        id: b.id,
        type: b.type as BlockPreview['type'],
        title: b.title,
        content: b.content,
        imagePath: b.imagePath,
        createdAt: b.createdAt,
        channelId: b.channelId,
      }));
  } else {
    result = await apiFetch<BlockPreview[]>(
      `/api/explore/previews?channelIds=${channelIds.map(encodeURIComponent).join(',')}`,
    );
  }

  if (blockPreviewCache.size >= MAX_PREVIEW_CACHE) {
    const firstKey = blockPreviewCache.keys().next().value;
    if (firstKey !== undefined) blockPreviewCache.delete(firstKey);
  }
  blockPreviewCache.set(cacheKey, { data: result, expiry: Date.now() + PREVIEW_CACHE_TTL });

  return result;
}

/** Look up one of the signed-in user's channels by slug. */
export async function fetchChannelBySlug(slug: string, _userId?: string): Promise<Channel> {
  const channels = await fetchChannels();
  const match = channels.find((ch) => ch.slug === slug);
  if (!match) throw new Error('Channel not found');
  return match;
}

export async function createChannel(input: CreateChannelInput): Promise<Channel> {
  const row = await apiFetch<ChannelListRow>('/api/channels', {
    method: 'POST',
    body: JSON.stringify({
      title: input.title,
      slug: input.slug,
      description: input.description || null,
      url: input.url || null,
      accentColor: input.accentColor || null,
      borderColor: input.borderColor || null,
      visibility: input.visibility || 'private',
      password: input.password || null,
    }),
  });
  invalidateAllCaches();
  clearBlockPreviewCache();
  return toChannelFromList(row);
}

function toChannelUpdateBody(updates: UpdateChannelInput): Record<string, unknown> {
  return {
    ...(updates.title !== undefined ? { title: updates.title } : {}),
    ...(updates.description !== undefined ? { description: updates.description } : {}),
    ...(updates.url !== undefined ? { url: updates.url } : {}),
    ...(updates.accentColor !== undefined ? { accentColor: updates.accentColor } : {}),
    ...(updates.borderColor !== undefined ? { borderColor: updates.borderColor } : {}),
    ...(updates.accent_color !== undefined ? { accentColor: updates.accent_color } : {}),
    ...(updates.border_color !== undefined ? { borderColor: updates.border_color } : {}),
  };
}

export async function updateChannel(id: string, updates: UpdateChannelInput): Promise<Channel> {
  const row = await apiFetch<ChannelListRow>(`/api/channels/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(toChannelUpdateBody(updates)),
  });
  invalidateAllCaches();
  clearBlockPreviewCache();
  return toChannelFromList(row);
}

export async function deleteChannel(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/channels/${encodeURIComponent(id)}`, { method: 'DELETE' });
  invalidateAllCaches();
  clearBlockPreviewCache();
}

export async function setChannelAccess(
  channelId: string,
  visibility: 'public' | 'private' | 'password' | 'hidden',
  password?: string | null,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/channels/${encodeURIComponent(channelId)}/access`, {
    method: 'POST',
    body: JSON.stringify({ visibility, password: password ?? null }),
  });
  invalidateAllCaches();
  clearBlockPreviewCache();
}
